import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { InvestmentInfo } from '../../../../../core/models/investment/investment-info';
import { DoubleDecimalPattern, IntegerPattern } from '../../../../../core/services/constant';

export interface InvestmentAmountSelection {
  lotQuantity: number;
  shareQuantity: number;
  investmentAmount: number;
  isValid: boolean;
}

/**
 * INC-238/INC-333 — lets an investor pick an investment amount via a slider kept in sync with a
 * manual share-count input. The step size (and therefore whether a given amount is valid) comes
 * from `investmentInfo.enableShareBasedIncrement`: off, top-ups must move by a full IncrementLot
 * lots (today's behavior); on, top-ups above the (still lot-based) minimum move by 1 share.
 */
@Component({
    selector: 'app-investment-amount-picker',
    templateUrl: './investment-amount-picker.component.html',
    styleUrls: ['./investment-amount-picker.component.scss'],
    standalone: false,
})
export class InvestmentAmountPickerComponent implements OnChanges {
  @Input() investmentInfo: InvestmentInfo;
  @Output() amountChange = new EventEmitter<InvestmentAmountSelection>();

  // Flag off: shareQuantity (in shares) is the source of truth, driven by the original Lot/Share
  // count field. Flag on: customAmount (in RM) is the source of truth instead, driven by the
  // "Enter your investment amount" field — shareQuantity is then derived
  // from it purely for the emitted selection (backend still wants a share/lot count either way).
  shareQuantity = 0;
  customAmount = 0;
  customAmountInput = '';
  DoubleDecimalPattern = DoubleDecimalPattern;
  IntegerPattern = IntegerPattern;
  errorKey: 'lessThanMin' | 'incrementFalse' | null = null;

  // Share-based mode only — whether the big input represents an RM amount or a raw share count,
  // plus the preset tier buttons shown above/below it (absolute share counts above the minimum).
  inputUnit: 'amount' | 'share' = 'share';
  // Defaults to the first tier (the bare minimum, tierShareValues[0]) rather than "Other" — the
  // investor lands on a valid, ready-to-submit amount without having to type anything.
  selectedTierIndex = 0;
  isCustomAmount = false;

  // INC-333: the 4 configurable values come from the campaign. Falls back to the original default of 1/2/5/10 IncrementShare steps
  // above the minimum, computed as absolute share counts, if the campaign data is missing/incomplete
  get tierShareValues(): number[] {
    const configured = this.investmentInfo?.tierIncrementSteps;
    if (configured?.length === 4) return [this.minimumShares, ...configured];

    const incrementShare = this.investmentInfo?.incrementShare || 1;
    return [
      this.minimumShares,
      ...[1, 2, 5, 10].map((steps) => this.minimumShares + steps * incrementShare),
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.investmentInfo && this.investmentInfo) {
      if (this.investmentInfo.enableShareBasedIncrement) {
        if (!this.customAmount) {
          this.customAmount = this.minimumInvestmentAmount;
          this.customAmountInput =
            this.inputUnit === 'amount' ? this.customAmount.toFixed(2) : this.sharesForDisplay.toFixed(0);
        }
      } else if (!this.shareQuantity) {
        this.shareQuantity = this.minimumShares;
      }
    }
    this.validateAndEmit();
  }

  get stepShares(): number {
    return this.unitSize * this.unitStep;
  }

  // Display unit the investor interacts with: flag off shows the original "Lot" field (1 unit =
  // 1 lot of shares); flag on shows the new "1 Share" field (1 unit = 1 share).
  get unitSize(): number {
    if (!this.investmentInfo) return 1;

    return this.investmentInfo.enableShareBasedIncrement ? 1 : this.investmentInfo.sharePerLot;
  }

  // How many display units make up one valid step: flag off snaps by IncrementLot lots (today's
  // behavior); flag on snaps by exactly 1 share.
  get unitStep(): number {
    if (!this.investmentInfo) return 1;

    return this.investmentInfo.enableShareBasedIncrement ? 1 : this.investmentInfo.incrementLot;
  }

  get displayValue(): number {
    return this.unitSize ? this.shareQuantity / this.unitSize : 0;
  }

  get displayMinimum(): number {
    return this.unitSize ? this.minimumShares / this.unitSize : 0;
  }

  get displayMaximum(): number {
    return this.unitSize ? this.maximumShares / this.unitSize : 0;
  }

  get minimumShares(): number {
    if (!this.investmentInfo) return 0;

    return this.investmentInfo.minimumLots * this.investmentInfo.sharePerLot;
  }

  get maximumShares(): number {
    if (!this.investmentInfo) return 0;

    const availableShares = this.investmentInfo.availableLots * this.investmentInfo.sharePerLot;

    // The slider shouldn't offer more than the campaign's funding goal, even if there's still
    // headroom up to the (higher) oversubscription-inclusive max target that availableLots allows.
    return Math.min(availableShares, this.fundingGoalShares);
  }

  get fundingGoalShares(): number {
    if (!this.investmentInfo?.pricePerShare) return 0;

    return Math.floor(this.investmentInfo.minimumTargetAmount / this.investmentInfo.pricePerShare);
  }

  get lotQuantity(): number {
    if (!this.investmentInfo?.sharePerLot) return 0;

    return this.shareQuantity / this.investmentInfo.sharePerLot;
  }

  get investmentAmount(): number {
    if (!this.investmentInfo) return 0;
    if (this.investmentInfo.enableShareBasedIncrement) return this.customAmount;

    return this.shareQuantity * this.investmentInfo.pricePerShare;
  }

  // RM value of a single share — used purely to convert between RM and share counts (typing in
  // share mode, tier values, display). NOT the top-up step size — see incrementAmount below.
  get sharePrice(): number {
    return this.investmentInfo?.pricePerShare || 0;
  }

  // RM value of the campaign's configurable share-based top-up step (IncrementShare shares,
  // defaults to 1 when unset). This is what "the increment" actually means for validation and for
  // the +/- buttons — distinct from sharePrice once IncrementShare > 1.
  get incrementAmount(): number {
    const incrementShare = this.investmentInfo?.incrementShare || 1;
    return this.sharePrice * incrementShare;
  }

  get minimumInvestmentAmount(): number {
    return this.minimumShares * (this.investmentInfo?.pricePerShare || 0);
  }

  // Preset tier buttons — the minimum plus the campaign's 4 configured absolute share counts,
  // expressed in whichever unit (RM amount or share count) the investor currently has selected
  // via the toggle. Each configured value is already on the valid increment lattice by
  // construction (AdminWeb/backend validate that at save time) — no rounding/snapping needed
  // here, and no risk of a preset failing its own validation the moment it's clicked.
  get shareTiers(): number[] {
    if (!this.investmentInfo) return [];

    return this.tierShareValues;
  }

  get amountTiers(): number[] {
    return this.shareTiers.map((shares) => shares * this.sharePrice);
  }

  get activeTiers(): number[] {
    return this.inputUnit === 'amount' ? this.amountTiers : this.shareTiers;
  }

  // RM value for the summary card — shares-based mode uses customAmount directly, lot-based
  // mode derives it from shareQuantity * pricePerShare
  get displayAmount(): number {
    if (this.investmentInfo?.enableShareBasedIncrement) return this.customAmount;
    return this.investmentAmount;
  }

  // Share count for display only, in share-based mode — derived from the typed RM amount rather
  // than the other way around.
  get sharesForDisplay(): number {
    if (!this.investmentInfo) return 0;
    if (!this.investmentInfo.enableShareBasedIncrement) return this.shareQuantity;

    return this.sharePrice ? Math.floor(this.customAmount / this.sharePrice) : 0;
  }

  // ── Slider position helpers (0–100 %) ────────────────────────────────────

  // How far the min value sits from the left edge of the track as a percentage.
  // Shifts right so the thumb never overflows when the range is small.
  get minOffsetPercent(): number {
    const range = this.displayMaximum - this.displayMinimum;
    if (range <= 0) return 0;
    const thumbHalf = (this.unitStep / 2) / range * 100;
    return Math.min(thumbHalf, 42);
  }

  // Width of the green active portion as a percentage.
  get trackWidthPercent(): number {
    const range = this.displayMaximum - this.displayMinimum;
    if (range <= 0) return 100;
    return Math.min(((this.displayValue - this.displayMinimum) / range) * 100, 100);
  }

  // Thumb left position as a percentage (relative to the full track width).
  get thumbPercent(): number {
    return this.minOffsetPercent + this.trackWidthPercent;
  }

  // ── Interaction handlers ─────────────────────────────────────────────────

  onSliderChange(displayValue: number) {
    this.shareQuantity = displayValue * this.unitSize;
    this.validateAndEmit();
  }

  onManualInput(rawValue: string) {
    this.shareQuantity = (Number(rawValue) || 0) * this.unitSize;
    this.validateAndEmit();
  }

  // Share-based mode only — mirrors the reference design's sanitization: digits + at most one
  // decimal point, at most 2 decimal places (RM amounts don't go finer than cents). When
  // inputUnit is 'share', only plain integers are accepted (no decimal point). The
  // `digitOnly` directive + conditional `[decimal]`/[pattern] on the <input> element prevent
  // malformed entry at the DOM level; this handler only runs on already-sanitized strings.
  onAmountInput(rawValue: string) {
    const pattern = this.inputUnit === 'share' ? IntegerPattern : DoubleDecimalPattern;
    if (rawValue && !pattern.test(rawValue)) return;

    this.customAmountInput = rawValue;
    const numeric = parseFloat(rawValue) || 0;
    // The underlying source of truth (customAmount) is always RM — when the investor is typing
    // in share-count mode, convert using the per-share price before storing/validating.
    this.customAmount = this.inputUnit === 'share' ? numeric * this.sharePrice : numeric;
    this.isCustomAmount = true;
    this.selectedTierIndex = null;
    this.validateAndEmit();
  }

  // Switches whether the big input (and the tier buttons under it) read/accept RM or share count.
  // Re-renders the current amount in the new unit rather than resetting it.
  setInputUnit(unit: 'amount' | 'share') {
    if (this.inputUnit === unit) return;

    this.inputUnit = unit;
    this.customAmountInput =
      unit === 'amount' ? this.customAmount.toFixed(2) : this.sharesForDisplay.toFixed(0);
  }

  selectTier(index: number) {
    this.selectedTierIndex = index;
    this.isCustomAmount = false;

    const tierValue = this.activeTiers[index];
    this.customAmount = this.inputUnit === 'amount' ? tierValue : tierValue * this.sharePrice;
    this.customAmountInput =
      this.inputUnit === 'amount' ? this.customAmount.toFixed(2) : tierValue.toFixed(0);
    this.validateAndEmit();
  }

  selectOtherAmount() {
    this.isCustomAmount = true;
    this.selectedTierIndex = null;
  }

  // +/- now jump straight to the next/previous *valid* amount instead of blindly adding/
  // subtracting one increment — so they double as the "fix an invalid typed amount" action the
  // error-correction-hint used to provide (RM10 -> "+" -> the minimum; RM580 -> "+" -> the
  // nearest valid increment above it). Decrement is a no-op below the minimum — see
  // decrementDisabled — since there's no lower valid amount to move to.
  adjustCustomAmount(direction: 1 | -1) {
    if (direction === -1 && this.decrementDisabled) return;

    this.customAmount = this.nextValidAmount(this.customAmount, direction);
    this.customAmountInput =
      this.inputUnit === 'amount' ? this.customAmount.toFixed(2) : this.sharesForDisplay.toFixed(0);
    this.isCustomAmount = true;
    this.selectedTierIndex = null;
    this.validateAndEmit();
  }

  get decrementDisabled(): boolean {
    return this.customAmount <= this.minimumInvestmentAmount + 0.005;
  }

  /** Handle clicks anywhere on the track to jump the thumb to that position. */
  onTrackClick(event: MouseEvent) {
    if (!this.investmentInfo) return;

    const track = (event.currentTarget as HTMLElement).closest('.slider-track');
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const trackWidth = rect.width;
    const ratio = Math.max(0, Math.min(1, clickX / trackWidth));

    const range = this.displayMaximum - this.displayMinimum;
    const rawValue = this.displayMinimum + ratio * range;
    // Snap to the nearest valid step.
    const snapped = Math.round(rawValue / this.unitStep) * this.unitStep;
    this.onSliderChange(snapped);
  }

  // The nearest valid RM amount to `amount`: floored at the minimum, then snapped to the nearest
  // whole IncrementShare step above it. Used to validate whether the typed amount is already
  // there. Reconstructing from a rounded step count, rather than taking `amount % incrementAmount`
  // directly, sidesteps float subtraction/addition noise (e.g. 584.5 - 559.45 =
  // 25.049999999999997) landing the raw remainder right next to the increment instead of at 0.
  private nearestValidAmount(amount: number): number {
    if (!this.incrementAmount) return this.minimumInvestmentAmount;

    const stepsAboveMin = Math.max(0, Math.round((amount - this.minimumInvestmentAmount) / this.incrementAmount));
    return Math.round((this.minimumInvestmentAmount + stepsAboveMin * this.incrementAmount) * 100) / 100;
  }

  // The next valid RM amount strictly beyond `amount` in the given direction — floored at the
  // minimum either way, so `-1` from just above the minimum lands back on the minimum rather than
  // going lower. Used by the +/- buttons: this always MOVES (unlike nearestValidAmount, which can
  // return the same amount if it's already on-lattice), and always in the requested direction
  // (unlike round-to-nearest, which could move the "wrong" way for an off-lattice amount closer to
  // the lower step). A small epsilon guards against an on-lattice amount's raw step count landing
  // just under its true integer (e.g. 584.5 - 559.45 = 25.049999999999997 → ~0.999999997 steps,
  // not 1) and being floored/ceiled to the wrong side.
  private nextValidAmount(amount: number, direction: 1 | -1): number {
    if (!this.incrementAmount) return this.minimumInvestmentAmount;

    const EPS = 1e-6;
    const rawSteps = (amount - this.minimumInvestmentAmount) / this.incrementAmount;
    const steps = Math.max(
      0,
      direction === 1 ? Math.floor(rawSteps + EPS) + 1 : Math.ceil(rawSteps - EPS) - 1,
    );
    return Math.round((this.minimumInvestmentAmount + steps * this.incrementAmount) * 100) / 100;
  }

  private validateAndEmit() {
    if (!this.investmentInfo) return;

    if (this.investmentInfo.enableShareBasedIncrement) {
      if (this.customAmount < this.minimumInvestmentAmount) {
        this.errorKey = 'lessThanMin';
      } else {
        // Validate in RM terms directly (matching the amount the investor actually typed) rather
        // than converting to shares first — avoids floating-point division artifacts (e.g.
        // 12,257.50 / 7.50) incorrectly passing or failing the multiple-of-increment check.
        const matchesIncrement = Math.abs(this.customAmount - this.nearestValidAmount(this.customAmount)) < 0.005;
        this.errorKey = matchesIncrement ? null : 'incrementFalse';
      }
    } else {
      if (this.shareQuantity < this.minimumShares) {
        this.errorKey = 'lessThanMin';
      } else if ((this.shareQuantity - this.minimumShares) % this.stepShares !== 0) {
        this.errorKey = 'incrementFalse';
      } else {
        this.errorKey = null;
      }
    }
    
    const emittedShareQuantity = this.investmentInfo.enableShareBasedIncrement
      ? this.sharePrice
        ? Math.round(this.customAmount / this.sharePrice)
        : 0
      : this.shareQuantity;

    this.amountChange.emit({
      lotQuantity: this.investmentInfo.sharePerLot
        ? emittedShareQuantity / this.investmentInfo.sharePerLot
        : 0,
      shareQuantity: emittedShareQuantity,
      investmentAmount: this.investmentAmount,
      isValid: this.errorKey === null,
    });
  }
}
